import * as Sentry from '@sentry/react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import { usePostAuthForgotPasswordMutation } from '@/services';
import { toast } from '@/components/ui/toaster';

export const Route = createFileRoute('/_unauthenticated/forgot-password')({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotPassword] = usePostAuthForgotPasswordMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword({ body: { email } }).unwrap();
      setSent(true);
      toast.success('Password reset email sent!');
    } catch (error: unknown) {
      Sentry.captureEvent(error as Error, { originalException: error, data: { email },  });
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-12">
        <div className="w-full max-w-md space-y-8 text-center">
          <h2 className="text-2xl font-semibold">Check your email</h2>
          <p className="text-muted-foreground">
            If an account exists for <b>{email}</b>, a password reset link has been sent.
          </p>
          <div className="flex flex-col gap-2">
            <Button variant="outline" onClick={() => setSent(false)}>
              Resend email
            </Button>
            <Link to="/login" className="hover:underline text-primary">
              Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-semibold">Forgot your password?</h1>
          <p className="text-muted-foreground">Enter your email to receive a reset link.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium">
              Email
            </label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Send reset link'
            )}
          </Button>
          <div className="text-center">
            <Link to="/login" className="hover:underline text-primary">
              Back to login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
